import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children, user }) {
  const [features, setFeatures] = useState({
    plan: 'free',
    max_projects: 3,
    max_users: 1,
    time_tracking: false,
    ai_features: false,
    advanced_reports: false,
    priority_support: false,
    risk_management: false,
    resource_planning: false,
    requirements_catalog: false,
    change_management: false,
    is_trial: false,
    trial_days_remaining: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchFeatures();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchFeatures = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/features`);
      setFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription features', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshFeatures = () => {
    fetchFeatures();
  };

  // Helper functions
  const canUseTimeTracking = () => features.time_tracking;
  const canUseAIFeatures = () => features.ai_features;
  const canUseAdvancedReports = () => features.advanced_reports;
  const canUseRiskManagement = () => features.risk_management;
  const canUseResourcePlanning = () => features.resource_planning;
  const canUseRequirementsCatalog = () => features.requirements_catalog;
  const canUseChangeManagement = () => features.change_management;
  const canCreateProject = (currentCount) => {
    if (features.max_projects === -1) return true;
    return currentCount < features.max_projects;
  };
  
  const isPro = () => features.plan === 'pro' || features.plan === 'business';
  const isBusiness = () => features.plan === 'business';
  const isFree = () => features.plan === 'free';

  const value = {
    features,
    loading,
    refreshFeatures,
    canUseTimeTracking,
    canUseAIFeatures,
    canUseAdvancedReports,
    canUseRiskManagement,
    canUseResourcePlanning,
    canUseRequirementsCatalog,
    canUseChangeManagement,
    canCreateProject,
    isPro,
    isBusiness,
    isFree
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

export default SubscriptionContext;
